import { useEffect } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { renewWaiterToken } from './waiterSlice';
import type { WaiterAuth } from './waiterSlice';
import { useRenewTokenMutation } from './waiterApi';

/** How often the remaining lifetime is looked at. Cheap: a check that finds
 *  enough life left costs nothing — no request goes out. */
export const RENEWAL_CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** Renew once less than this is left. Half of the server's flat 12h
 *  WAITER_TOKEN_TTL, so a device used at least once every ~6h never expires,
 *  and a device that renewed then went dark still has 12h of runway. */
export const RENEWAL_THRESHOLD_MS = 6 * 60 * 60 * 1000;

/**
 * SC-799 — slides the waiter token's expiry forward while the device is in
 * service, so the handheld stops falling back to the PIN pad every morning.
 *
 * The two things an owner reads as one are NOT one: `idle_lock_seconds` (« ne
 * jamais verrouiller ») only disarms the inactivity timer (see `useIdleLock`),
 * while the token itself expired on a flat 12h window that nothing pushed
 * back. This hook is the missing half: it re-signs through `renew/` before
 * that window closes.
 *
 * Called from every surface that holds a live auth (WaiterShell, WaiterBar) —
 * a waiter parked on a table for a whole service never crosses the shell.
 */
export function useWaiterTokenRenewal(auth: WaiterAuth | null): void {
  const dispatch = useAppDispatch();
  const [renew] = useRenewTokenMutation();
  const restaurantUuid = auth?.restaurantUuid;
  const token = auth?.token;
  const expiresAt = auth?.expiresAt;

  useEffect(() => {
    if (!restaurantUuid || !token || !expiresAt) return undefined;

    let cancelled = false;

    const maybeRenew = async () => {
      const remaining = new Date(expiresAt).getTime() - Date.now();
      if (Number.isNaN(remaining) || remaining > RENEWAL_THRESHOLD_MS) return;
      try {
        const result = await renew({ token }).unwrap();
        if (!cancelled) {
          dispatch(
            renewWaiterToken({
              restaurantUuid,
              token: result.token,
              expiresAt: result.expiresAt,
            }),
          );
        }
      } catch {
        // Jamais de deconnexion depuis ici : un echec passager (hors ligne,
        // requete annulee) ne doit pas jeter le serveur au pave PIN alors que
        // son jeton court toujours, et un vrai 403 WAITER_REVOKED est deja
        // traite par les surfaces qui pollent (TablesOverview). Reessaye au
        // tic suivant.
      }
    };

    maybeRenew();
    const id = window.setInterval(maybeRenew, RENEWAL_CHECK_INTERVAL_MS);
    // Un terminal de salle passe son service en arriere-plan, ou les timers
    // sont brides voire geles : le retour au premier plan est le tic fiable.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRenew();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch, renew, restaurantUuid, token, expiresAt]);
}
